import { createAsyncThunk } from "@reduxjs/toolkit";
import { ApiError } from "@vana/core";
import { type NewAddressDraft } from "../../../../../shared/geo/new-address-draft";
import { type FailureAction } from "../../../domain/entities/failure-reason";
import { type FailedDeliveryDispatch } from "../../config";
import { type ExtraArgument } from "../../extra-argument";
import { type State } from "../../state";
import { thunkTypes } from "../thunk-types";

export interface SubmitFailedDeliveryParams {
  requestId: string;
  /** The request's current state — drives whether we start / record the attempt. */
  currentState: string;
  /** Resolved action: reagendar (customer asked to move it → released to the
   *  pool) while attempts remain; report (a plain failed attempt) for the
   *  no-answer reasons; undeliverable for a problem another blind visit cannot
   *  fix; rejected when the customer turned the card down (DMS-35). */
  action: FailureAction;
  /** Audited reason text (motive label + optional note). */
  reason: string;
  /** The structured motive. The backend acts on it: sent with the failed
   *  attempt, a wrong address or a DPI problem parks the delivery right there,
   *  so this screen does not have to ask for that separately. */
  reasonCode: string;
  /** The day and shift agreed for a reschedule (DMS-47); omitted otherwise.
   *  No instant travels: the backend resolves the hours in Guatemala time. */
  deliveryDate?: string;
  deliveryShift?: "morning" | "afternoon";
  /** Chosen registered address for a reagendar; omitted to keep the current one. */
  addressId?: string;
  /** Address the ambassador captured in the field; registered with the
   *  transition (DMS-1). Wins over addressId when both are present. */
  newAddress?: NewAddressDraft;
  /** True when the new slot is today: the delivery stays with this ambassador
   *  instead of going back to the logistics pool (DMS-1). */
  keepWithAmbassador?: boolean;
  lat: number;
  long: number;
  /** Optional WhatsApp screenshot backing the reason. Leave it out when an
   *  earlier submit already uploaded it: a retry repeats only what failed. */
  evidence?: Blob;
  /** Told once the evidence is on file, so the screen can drop it from a retry
   *  (DMS2-72: one failed reschedule uploaded the same capture eight times). */
  onEvidenceUploaded?: () => void;
}

/**
 * Runs the whole "No pude entregar" sequence in order:
 *  1. start (assigned → in_route) when the request hasn't been started yet;
 *  2. failed-attempt (in_route → not_delivered) unless it already failed — this
 *     is where the failure reason is recorded and the attempt is counted;
 *  3. upload the evidence capture, if any;
 *  4. resolve the report:
 *     - reagendar for TODAY → reschedule (new slot + address, stays assigned to
 *       the same ambassador — he resolves his own day, DMS-1);
 *     - reagendar for another day → reschedule-to-pool (new slot + address,
 *       released to the pool for a supervisor to re-assign);
 *     - report        → nothing more (stays not_delivered for the supervisor);
 *     - undeliverable → nothing more when an attempt was just recorded (the
 *       backend parked it in that same call, and asking again is a 409); the
 *       explicit move is only for a re-submission over an already-failed
 *       attempt;
 *     - rejected      → the request closes and moves to Rechazos.
 *
 * A brand-new address travels with the reschedule call itself, so the backend
 * registers it in the same transaction: abandoning the screen registers nothing.
 *
 * The rejection carries the backend's error code (see recordCustody): the screen
 * explains a reschedule to an address with no delivery zone in the ambassador's
 * terms instead of the generic failure.
 */
export const submitFailedDelivery = createAsyncThunk<
  void,
  SubmitFailedDeliveryParams,
  { dispatch: FailedDeliveryDispatch; extra: ExtraArgument; state: State; rejectValue: string }
>(thunkTypes.SUBMIT_FAILED_DELIVERY, async (params, { extra, rejectWithValue }) => {
  try {
    await runSequence(params, extra);
  } catch (error) {
    return rejectWithValue(error instanceof ApiError ? (error.apiError?.error_code ?? "") : "");
  }
  return undefined;
});

const runSequence = async (params: SubmitFailedDeliveryParams, extra: ExtraArgument) => {
  const { failedDeliveryRepo: repo } = extra;
  const {
    requestId,
    currentState,
    action,
    reason,
    reasonCode,
    deliveryDate,
    deliveryShift,
    addressId,
    newAddress,
    keepWithAmbassador,
    lat,
    long,
    evidence,
    onEvidenceUploaded,
  } = params;

  if (currentState === "assigned") {
    await repo.start({ requestId, lat, long });
  }
  // The failed attempt carries the structured motive, and the backend resolves
  // the outcome in that same call: a rejection closes the request, a terminal
  // motive or an exhausted cap parks it. Both clear the assignment, so a
  // follow-up call would no longer reach the ambassador's own delivery — row
  // scoping answers 404, which is exactly what the field hit.
  const recordedAttempt = currentState !== "not_delivered";
  if (recordedAttempt) {
    await repo.failAttempt({ requestId, reason, reasonCode, lat, long });
  }
  if (evidence) {
    await repo.uploadEvidence({ requestId, image: evidence });
    onEvidenceUploaded?.();
  }
  if (action === "reschedule") {
    const move = { requestId, reason, deliveryDate, deliveryShift, addressId, newAddress, lat, long };
    if (keepWithAmbassador) {
      await repo.reschedule(move);
    } else {
      await repo.rescheduleToPool(move);
    }
  } else if (action === "rejected" && !recordedAttempt) {
    // Same reason as below: recording the attempt already closed it, and the
    // ambassador no longer owns the request, so a second call answers 404.
    await repo.rejectByCustomer({ requestId, note: reason, lat, long });
  } else if (action === "undeliverable" && !recordedAttempt) {
    // Re-submitting over an attempt that already failed: no new attempt was
    // recorded, so nothing parked it and the move has to be asked for.
    await repo.moveToUndeliverable({ requestId, reason, reasonCode, lat, long });
  }
};
